<template>
  <div>
    <p class="small text-muted mb-2">Saved with the product. Leave a price blank to use the default price.</p>
    <p v-if="loading" class="small">Loading branches...</p>
    <div v-else-if="loadError" class="text-danger" role="alert">
      {{ loadError }} <button type="button" class="btn btn-sm btn-light" @click="$emit('retry')">Retry</button>
    </div>
    <template v-else>
      <p v-if="!branches.length" class="small text-muted">No branches available.</p>
      <div v-else>
        <div class="row small font-weight-bold border-bottom pb-2 d-none d-md-flex">
          <div class="col-md-4">BRANCH</div>
          <div v-for="field in fields" :key="field.key" class="col-md-4 text-right">
            <div class="form-row"><div class="col-8">{{ field.label }}</div></div>
          </div>
        </div>
        <div v-for="branch in branches" :key="branch.id" class="row border-bottom py-2">
          <div class="col-12 col-md-4 mb-2 mb-md-0">{{ branch.code }} - {{ branch.name }}</div>
          <div v-for="field in fields" :key="field.key" class="col-6 col-md-4">
            <div class="form-row align-items-center">
              <div class="col-8">
                <label class="d-md-none small" :for="inputId(branch.id, field.key)">{{ field.label }}</label>
                <input :id="inputId(branch.id, field.key)" class="form-control" type="number"
                  min="0" max="9999999.99" step="0.01" :value="price(branch.id, field.key)"
                  :placeholder="formatPrice(defaults[field.key])" :aria-label="branch.name + ' ' + field.label"
                  :class="{ 'is-invalid': error(branch.id, field.key) }" :aria-invalid="!!error(branch.id, field.key)"
                  :aria-describedby="hasPrice(price(branch.id, field.key)) ? inputId(branch.id, field.key) + '-default' : null"
                  @input="setPrice(branch.id, field.key, $event)">
              </div>
              <div class="col-4 small text-muted">
                <span v-if="hasPrice(price(branch.id, field.key))" :id="inputId(branch.id, field.key) + '-default'">
                  Default<br>{{ hasPrice(defaults[field.key]) ? 'R' + formatPrice(defaults[field.key]) : 'Not set' }}
                </span>
              </div>
            </div>
            <div v-if="error(branch.id, field.key)" class="small text-danger mt-1" role="alert">{{ error(branch.id, field.key) }}</div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  props: {
    value: { type: Array, default: () => [] },
    prices: { type: Array, default: () => [] },
    branches: { type: Array, default: () => [] },
    defaults: { type: Object, required: true },
    loading: Boolean,
    loadError: String,
    errors: Object,
  },
  data() {
    return {
      localErrors: {},
      fields: [
        { key: 'mandate_pricing_min', label: 'MIN (Mandate)' },
        { key: 'mandate_pricing_max', label: 'MAX (List Price)' },
      ],
    };
  },
  methods: {
    inputId(branchId, field) { return `product-branch-${this._uid}-${branchId}-${field}`; },
    hasPrice(value) { return value !== null && value !== undefined && value !== ''; },
    formatPrice(value) { return this.hasPrice(value) ? Number(value).toFixed(2) : ''; },
    savedPrice(branchId, field) {
      const saved = this.prices.find(row => String(row.branch_id) === String(branchId));
      return saved && saved[field] != null ? saved[field] : null;
    },
    price(branchId, field) {
      const change = this.value.find(row => String(row.branch_id) === String(branchId));
      return change && Object.prototype.hasOwnProperty.call(change, field) ? change[field] : this.savedPrice(branchId, field);
    },
    validPrice(value) {
      return !this.hasPrice(value) || (/^\d+(\.\d{1,2})?$/.test(String(value)) && Number(value) <= 9999999.99);
    },
    setPrice(branchId, field, event) {
      const value = event.target.value === '' ? null : event.target.value;
      const valid = !event.target.validity.badInput && this.validPrice(value);
      this.$set(this.localErrors, `${branchId}.${field}`, valid ? '' : 'Enter a valid price with up to two decimal places.');
      const saved = this.savedPrice(branchId, field);
      const same = value === null ? saved === null : valid && saved !== null && Number(value) === Number(saved);
      const changes = this.value.map(row => ({ ...row }));
      let change = changes.find(row => String(row.branch_id) === String(branchId));
      if (!change) {
        change = { branch_id: branchId };
        changes.push(change);
      }
      if (same) delete change[field];
      else change[field] = value;
      this.$emit('input', changes.filter(row => Object.keys(row).length > 1));
    },
    error(branchId, field) {
      const local = this.localErrors[`${branchId}.${field}`];
      const index = this.value.findIndex(row => String(row.branch_id) === String(branchId));
      const server = this.errors && this.errors[`branch_prices.${index}.${field}`];
      return local || (server ? server.join(' ') : '');
    },
    validate() {
      return !Object.values(this.localErrors).some(Boolean) && this.value.every(row =>
        this.fields.every(field => this.validPrice(row[field.key])));
    },
  },
};
</script>
